import { z } from "zod";

const ORGANIZATION_TYPES = [
  "community",
  "club",
  "federation",
  "business",
] as const;

const PHONE_MIN_LENGTH = 10;
const PHONE_MAX_LENGTH = 11;
const ORGANIZER_ADDRESS_MAX_LENGTH = 150;
const ORGANIZER_CONTACT_POSITION_MAX_LENGTH = 50;

const hasWhitespace = (value: string): boolean => {
  for (const char of value) {
    if (char === " " || char === "\t" || char === "\n") return true;
  }
  return false;
};

const isValidEmail = (email: string): boolean => {
  if (!email) return false;
  if (hasWhitespace(email)) return false;
  const parts = email.split("@");
  if (parts.length !== 2) return false;
  const [local, domain] = parts;
  if (!local || !domain) return false;
  if (domain.startsWith(".") || domain.endsWith(".")) return false;
  if (!domain.includes(".")) return false;
  return true;
};

const onlyDigits = (value: string): string => {
  let result = "";
  for (const char of value) {
    if (char >= "0" && char <= "9") {
      result += char;
    }
  }
  return result;
};

const isValidPhone = (value: string): boolean => {
  const digits = onlyDigits(value);
  return digits.length >= PHONE_MIN_LENGTH && digits.length <= PHONE_MAX_LENGTH;
};

const isHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

const socialUrlSchema = z
  .string()
  .optional()
  .or(z.literal(""))
  .refine((value) => value === "" || value === undefined || isHttpUrl(value), {
    message: "Link phải bắt đầu bằng http:// hoặc https://",
  });

/**
 * Layer 1: Domain Core Schema
 * Shared between Backend and Frontend.
 * Validates "Clean Data" format.
 */
export const organizerRegistrationSchema = z.object({
  // General Info
  name: z
    .string()
    .min(1, "Tên tổ chức là bắt buộc")
    .max(200, "Tên tổ chức không được vượt quá 200 ký tự"),
  type: z
    .string()
    .min(1, "Loại tổ chức là bắt buộc")
    .refine(
      (value) =>
        ORGANIZATION_TYPES.includes(
          value as (typeof ORGANIZATION_TYPES)[number],
        ),
      {
        message: "Loại tổ chức không hợp lệ",
      },
    ),

  // Address
  city: z.string().min(1, "Vui lòng chọn tỉnh/thành phố"),
  district: z.string().min(1, "Vui lòng chọn quận/huyện"),
  address: z
    .string()
    .min(1, "Vui lòng nhập địa chỉ chi tiết")
    .max(ORGANIZER_ADDRESS_MAX_LENGTH, "Địa chỉ không được vượt quá 150 ký tự"),

  // Contact
  contact_person: z.string().min(1, "Vui lòng nhập tên người liên hệ"),
  contact_position: z
    .string()
    .min(1, "Vui lòng nhập chức vụ")
    .max(
      ORGANIZER_CONTACT_POSITION_MAX_LENGTH,
      "Chức vụ không được vượt quá 50 ký tự",
    ),
  contact_email: z
    .string()
    .min(1, "Vui lòng nhập email liên hệ")
    .refine((value) => isValidEmail(value), { message: "Email không hợp lệ" }),
  contact_phone: z
    .string()
    .min(1, "Vui lòng nhập số điện thoại")
    .refine((value) => isValidPhone(value), {
      message: "Số điện thoại không hợp lệ",
    }),

  // Legal
  license_type: z.string().min(1, "Loại giấy tờ là bắt buộc"),
  description: z.string().optional(),

  // Optional / Files
  logo_url: z.string().optional(),
  logoFile: z.any().optional(),
  verificationFiles: z
    .array(z.unknown())
    .min(1, "Vui lòng tải lên ít nhất 1 giấy tờ xác minh"),

  // Social
  fanpage_url: socialUrlSchema,
  website_url: socialUrlSchema,
});

export type OrganizerRegistrationForm = z.infer<
  typeof organizerRegistrationSchema
>;
